import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatRipple } from '@angular/material/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatToolbarModule } from '@angular/material/toolbar';
import { Router, RouterModule } from '@angular/router';
import { Popover, PopoverModule } from 'primeng/popover';
import { Subject } from 'rxjs';
import { distinctUntilChanged, take, takeUntil } from 'rxjs/operators';
import { CompanyWithImpersonatableUsers, ImpersonatableUser, MenuItem, UserInfo } from '../../shared/models';
import { Notification as SystemNotification } from '../../shared/models/common/notification';
import { AuthenticationService } from '../authentication/authentication.service';
import { UserService } from '../http';
import { NotificationService } from '../http/notification.service';
import { ReportsService } from '../http/reports.service';
import { LoggedUserService } from '../services/logged-user.service';
import { MapService } from '../services/map.service';
import { MenuService } from '../services/menu.service';
import { OverlayPanelService } from '../services/overlay-panel.service';
import { SideNavService } from '../services/side-nav.service';
import { SignalrService } from '../services/signalr.service';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    RouterModule,
    MatBadgeModule,
    MatMenuModule,
    PopoverModule,
    MatListModule,
    CommonModule,
    MatRipple,
    MatExpansionModule,
    FormsModule,
    MatInputModule,
    MatFormFieldModule,
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent implements OnInit, OnDestroy {
  notifications: SystemNotification[] = [];
  notificationsCount = 0;
  downloadingNotificationId: string | null = null;
  userInfo: UserInfo;
  private readonly destroy$ = new Subject<void>();

  menu: MenuItem[] = [];

  currentTime = '';
  currentDate = '';

  companies: CompanyWithImpersonatableUsers[] = [];
  expandedCompanyId: number | null = null;
  companyUserSearch: string = '';
  filteredCompanies: CompanyWithImpersonatableUsers[] = [];
  private readonly companyUserSearch$ = new Subject<string>();

  // Get references to overlay panels
  @ViewChild('op') notificationsPanel?: Popover;
  @ViewChild('imp') impersonationPanel?: Popover;
  @ViewChild('accountShares') accountShares?: Popover;

  constructor(
    readonly menuService: MenuService,
    readonly singlarService: SignalrService,
    readonly loggedUserService: LoggedUserService,
    readonly authenticationService: AuthenticationService,
    readonly notificationService: NotificationService,
    readonly reportsService: ReportsService,
    readonly sideNavService: SideNavService,
    readonly userService: UserService,
    private readonly router: Router,
    private readonly overlayPanelService: OverlayPanelService,
    private readonly mapService: MapService,
    private readonly dialog: MatDialog,
  ) {
    this.userInfo = this.loggedUserService.loggedUserInfo;
    this.singlarService.startConnection().then(() => {
      this.singlarService.addTripListner();

      this.singlarService.tripChangeSubject.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.getNotifications();
      });
    });
  }

  ngOnInit(): void {
    this.userService
      .getImpersonationUsers()
      .pipe(take(1))
      .subscribe((data) => {
        if (data?.companys?.length > 0) {
          this.companies = data.companys;
          this.filteredCompanies = data.companys;
        }
      });

    this.menuService.menuItems$.pipe(takeUntil(this.destroy$)).subscribe((items) => {
      this.menu = items;
    });

    this.overlayPanelService.closeAllOverlays$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.notificationsPanel) {
        this.notificationsPanel.hide();
      }
      if (this.impersonationPanel) {
        this.impersonationPanel.hide();
      }
    });

    this.getNotifications();
    this.updateTime();

    setInterval(() => this.updateTime(), 60000);
    this.companyUserSearch$.pipe(distinctUntilChanged()).subscribe((search) => {
      if (!search) {
        this.filteredCompanies = this.companies;
        return;
      }
      const searchTerm = search.toLowerCase().trim();
      this.filteredCompanies = this.companies
        .map((company) => {
          // Spread keeps every user field (including the type) on filtered results.
          const matchingUsers = (company.users || []).filter(
            (user) =>
              user.username.toLowerCase().includes(searchTerm) ||
              (user.fullName || '').toLowerCase().includes(searchTerm),
          );

          if (company.name.toLowerCase().includes(searchTerm)) {
            return company;
          } else if (matchingUsers.length > 0) {
            return {
              ...company,
              users: matchingUsers,
            };
          }
          return null;
        })
        .filter((company): company is CompanyWithImpersonatableUsers => company !== null);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.singlarService.stopConnection();
  }

  isActive(url: string): boolean {
    return this.router.isActive('/' + url, {
      paths: 'exact',
      queryParams: 'ignored',
      matrixParams: 'ignored',
      fragment: 'ignored',
    });
  }

  getAccountName(): string {
    const { name, firstSurname, lastSurname } = this.userInfo.user;
    return `${name} ${firstSurname} ${lastSurname}`;
  }

  getImpersonateName(): string {
    return this.userInfo.user.impersonatedBy ?? 'Usuario sin nombre';
  }

  getInitials(text: string): string {
    return text
      .split(' ')
      .slice(0, 2)
      .map((word) => word.charAt(0).toUpperCase())
      .join('');
  }

  getUserTypeLabel(user: ImpersonatableUser): string {
    return this.isAdministrator(user) ? 'Administrador' : 'Limitado';
  }

  isAdministrator(user: ImpersonatableUser): boolean {
    return user.isAdmin;
  }

  hasPermission(
    permission: string,
    type: 'chat' | 'globalSim' | 'shipment' | 'phonesAdmin' | 'innovaPlatform' | 'reports' | 'monitor',
  ): boolean {
    if (!this.userInfo) {
      return false;
    }

    return this.userInfo.permissions[type]?.includes(permission) || false;
  }

 
 

  private getNotifications() {
    this.notificationService
      .getNotifications()
      .pipe(takeUntil(this.destroy$))
      .subscribe((notifications) => {
        this.notifications = notifications;
        this.notificationsCount = this.notifications.filter((n) => !n.isDone).length;
      });
  }

  toggleSideNav() {
    this.sideNavService.toggle();
  }

  onDoneNotification(id: string) {
    this.notificationService
      .setNotificationDone(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.getNotifications();
      });
  }

  // entityId doubles as the report job id — presence of it is what tells a
  // succeeded export apart from a failed one, not the message wording.
  downloadReportNotification(notification: SystemNotification) {
    if (!notification.entityId || this.downloadingNotificationId) return;

    this.downloadingNotificationId = notification.notificationId;
    this.reportsService.downloadReportJob(notification.entityId, 'Historial TPMS', () => {
      this.downloadingNotificationId = null;
    });
  }

  updateTime() {
    const now = new Date();
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    };

    // Actualizar la hora
    this.currentTime = now.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });

    // Actualizar la fecha
    this.currentDate = now.toLocaleDateString([], options);
  }

  onUserClick(user: ImpersonatableUser) {
    this.userService
      .impersonateUser(user.id)
      .pipe(take(1))
      .subscribe(() => {
        this.userService
          .getLoggedUserInfo()
          .pipe(take(1))
          .subscribe((userInfo) => {
            // Check if user is being impersonated
            if (userInfo.user.linkerUserId !== userInfo.user.innovaUserId) {
              userInfo.user.impersonatedBy = user.fullName;
              userInfo.user.isImpersonated = true;
            }

            this.mapService.clearDashboardMap();
            this.loggedUserService.loggedUserInfo = userInfo;
            window.location.reload();
          });
      });
  }

  toggleCompany(companyId: number) {
    this.expandedCompanyId = this.expandedCompanyId === companyId ? null : companyId;
  }

  onInputCompanyUserSearch(event: Event | string) {
    const value = typeof event === 'string' ? event : (event.target as HTMLInputElement).value;
    this.companyUserSearch = value;
    this.companyUserSearch$.next(value);
  }

  navigateAccount() {
    this.router.navigate(['/profile']);
  }

  logout() {
    const userInfo = this.loggedUserService.loggedUserInfo;
    this.overlayPanelService.closeAll();
    this.dialog.closeAll();

    if (userInfo?.user?.isImpersonated) {
      this.userService
        .resetImpersonate()
        .pipe(take(1))
        .subscribe(() => {
          this.mapService.clearDashboardMap();
          this.authenticationService.logout();
          // this.toggleMenu();
        });
    } else {
      this.authenticationService.logout();
      // this.toggleMenu();
    }
  }

  resetImpersonation() {
    this.userService
      .resetImpersonate()
      .pipe(take(1))
      .subscribe(() => {
        this.userService
          .getLoggedUserInfo()
          .pipe(take(1))
          .subscribe((userInfo) => {
            this.mapService.clearDashboardMap();
            this.loggedUserService.loggedUserInfo = userInfo;
            window.location.reload();
          });
      });
  }
}
